import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import ClientRealLayout from "./Layout";
import usePageMeta from "../hooks/usePageMeta";
import useAuth from "../hooks/useAuth";
import { BASE_URL } from "../config";
import { showSuccess, showError } from "../utils/dialog";
import { formatAppDateTime } from "../utils/appDateTime";
import { resolveBackendUrl } from "../utils/accountAvatarUrl";
import ResetPasswordForm from "../components/forms/ResetPasswordForm";
import TwoStepVerificationSection from "../components/twoFactor/TwoStepVerificationSection";
import UnsavedChangesModal from "../components/UnsavedChangesModal";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  getOsIcon,
  getBrowserIcon,
  getDeviceTypeIcon,
  formatDeviceInfo,
} from "../utils/deviceIcons";
import { useUnsavedChanges } from "../hooks/useUnsavedChanges";

const ClientMyAccount = () => {
  usePageMeta("Client My Account");
  const auth = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openModal, setOpenModal] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [pendingPath, setPendingPath] = useState(null);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    title: "",
    email: "",
    phone_mobile: "",
    profile_picture: "",
  });

  const [originalData, setOriginalData] = useState({});
  const [securityData, setSecurityData] = useState({
    email: "",
    last_signin: "",
    two_factor_enabled: false,
    devices: [],
  });

  // Load profile data
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        const accountId = auth?.accountId || localStorage.getItem("accountId");
        const token = auth?.token || localStorage.getItem("token");
        if (!accountId) return;

        const res = await fetch(`${BASE_URL}/api/my-account/account/me`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
        });

        const json = await res.json();
        if (json.success && json.data) {
          const d = json.data;
          // Ensure all values are strings to prevent [object Object] issues
          const formData = {
            first_name: String(d.first_name || "").trim(),
            last_name: String(d.last_name || "").trim(),
            title: String(d.title || d.preferred_name || "").trim(),
            email: String(d.email || "").trim(),
            phone_mobile: String(d.phone_mobile || d.phone || "").trim(),
            profile_picture: String(d.profile_picture || "").trim(),
          };
          setForm(formData);
          setOriginalData(formData);
        }
      } catch (e) {
        console.error("Failed to load profile", e);
      } finally {
        setLoading(false);
      }
    };

    const loadSecurity = async () => {
      try {
        const accountId = auth?.accountId || localStorage.getItem("accountId");
        const token = auth?.token || localStorage.getItem("token");
        if (!accountId) return;

        const res = await fetch(`${BASE_URL}/api/my-account/account/security`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
        });

        const json = await res.json();
        if (json.success && json.data) {
          setSecurityData(json.data);
        }
      } catch (e) {
        console.error("Failed to load security data", e);
      }
    };

    if (auth?.accountId || localStorage.getItem("accountId")) {
      loadProfile();
      loadSecurity();
    }
  }, [auth?.accountId]);

  const handleChange = (key) => (e) => {
    // Handle both event objects and direct values (e.g., from PhoneInput)
    let value;
    if (e && typeof e === 'object' && 'target' in e && e.target) {
      // It's an event object from a regular input
      value = e.target.value || "";
    } else if (typeof e === 'string' || typeof e === 'number') {
      // It's a direct value (e.g., from PhoneInput onChange)
      value = e;
    } else {
      // Fallback: try to extract value or use empty string
      value = "";
    }
    
    // Ensure value is a string (don't trim phone numbers as they may have spaces)
    const stringValue = key === 'phone_mobile' ? String(value || "") : String(value || "").trim();
    
    setForm((prev) => ({ ...prev, [key]: stringValue }));
    // Clear error when user starts typing
    if (formErrors[key]) {
      setFormErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!form.first_name?.trim()) {
      errors.first_name = "First Name is required";
    }
    if (!form.last_name?.trim()) {
      errors.last_name = "Last Name is required";
    }
    if (!form.email?.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = "Please enter a valid email address";
    }
    if (!form.phone_mobile?.trim()) {
      errors.phone_mobile = "Phone Number is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (shouldRedirect = false) => {
    if (!validateForm()) {
      showError("Please fix the errors before saving");
      return;
    }

    try {
      setSaving(true);
      const accountId = auth?.accountId || localStorage.getItem("accountId");
      const token = auth?.token || localStorage.getItem("token");
      if (!accountId) {
        showError("Account not found. Please login again.");
        return;
      }

      // Ensure all values are strings and clean them before sending
      const payload = {
        first_name: String(form.first_name || "").trim(),
        last_name: String(form.last_name || "").trim(),
        title: String(form.title || "").trim(),
        email: String(form.email || "").trim(),
        phone_mobile: String(form.phone_mobile || "").trim(),
      };

      const res = await fetch(`${BASE_URL}/api/my-account/account/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      if (json.success) {
        setOriginalData({ ...form });
        showSuccess("Profile updated successfully");
        
        // If redirecting after save, proceed with navigation
        if (shouldRedirect && pendingPath) {
          proceedNavigation();
          navigate(pendingPath);
          setPendingPath(null);
          setOpenModal(null);
        }
      } else {
        showError(json.message || "Failed to update profile");
      }
    } catch (e) {
      console.error("Save error", e);
      showError("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Handle unsaved changes modal actions
  const handleSaveAndRedirect = () => {
    handleSave(true);
  };

  const handleRedirectWithoutSaving = () => {
    if (effectivePendingPath) {
      proceedNavigation();
      setPendingPath(null);
      setOpenModal(null);
    }
  };

  const handleCancelUnsaved = () => {
    cancelNavigation();
    setPendingPath(null);
    setOpenModal(null);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setSaving(true);
      const token = auth?.token || localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(`${BASE_URL}/api/my-account/account/avatar`, {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });

      const json = await res.json();
      if (json.success && json.url) {
        const profileRes = await fetch(`${BASE_URL}/api/my-account/account/me`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
        });
        const profileJson = await profileRes.json().catch(() => ({}));
        const nextProfilePicture =
          profileJson?.success && profileJson?.data
            ? String(profileJson.data.profile_picture || "").trim()
            : json.url;
        setForm((prev) => ({ ...prev, profile_picture: nextProfilePicture }));
        showSuccess("Profile picture updated successfully");
      } else {
        showError(json.message || "Failed to upload profile picture");
      }
    } catch (err) {
      console.error("Avatar upload error:", err);
      showError("Failed to upload profile picture");
    } finally {
      setSaving(false);
      if (e.target) e.target.value = "";
    }
  };

  const handlePasswordReset = async ({ currentPassword, newPassword }) => {
    try {
      const token = auth?.token || localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}/api/my-account/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });
      const j = await res.json();
      if (!res.ok || !j?.success) {
        throw new Error(j?.message || "Password reset failed");
      }
      showSuccess(j.message || "Password updated successfully");
      setOpenModal(null);
    } catch (e) {
      console.error("Password reset error", e);
      showError(e.message || "Failed to update password");
      throw e;
    }
  };

  const handleRemoveDevice = async (deviceId) => {
    if (!window.confirm("Are you sure you want to remove this trusted device?"))
      return;

    try {
      const accountId = auth?.accountId || localStorage.getItem("accountId");
      const token = auth?.token || localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/api/my-account/account/security/device/${deviceId}?accountId=${accountId}`,
        {
          method: "DELETE",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      const j = await res.json();
      if (!j?.success) throw new Error(j?.message || "Device removal failed");

      setSecurityData((prev) => ({
        ...prev,
        devices: prev.devices.filter((d) => d.trusted_device_id !== deviceId),
      }));
      showSuccess("Device removed successfully");
    } catch (e) {
      console.error("Device removal error", e);
      showError("Failed to remove device");
    }
  };

  const fullName = useMemo(() => {
    const fn = form.first_name || "";
    const ln = form.last_name || "";
    return `${fn} ${ln}`.trim() || "Client contact";
  }, [form.first_name, form.last_name]);

  // Check if form has changes
  const hasChanges = useMemo(() => {
    if (!originalData || Object.keys(originalData).length === 0) {
      return false;
    }
    return (
      form.first_name !== (originalData.first_name || "") ||
      form.last_name !== (originalData.last_name || "") ||
      form.title !== (originalData.title || "") ||
      form.email !== (originalData.email || "") ||
      form.phone_mobile !== (originalData.phone_mobile || "")
    );
  }, [form, originalData]);

  // Handle navigation blocking for unsaved changes
  const {
    pendingNavigation,
    proceedNavigation,
    cancelNavigation,
    pendingPath: hookPendingPath,
  } = useUnsavedChanges(hasChanges, (pathname) => {
    setPendingPath(pathname);
    setOpenModal("unsaved");
  });

  // Use hook's pendingPath if available, otherwise use local state
  const effectivePendingPath = hookPendingPath || pendingPath;

  // Handle browser refresh/close with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = ""; // Chrome requires returnValue to be set
        return ""; // Some browsers require return value
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasChanges]);

  if (loading) {
    return (
      <ClientRealLayout>
        <div className="p-6 flex items-center justify-center">
          <div className="text-gray-500">Loading...</div>
        </div>
      </ClientRealLayout>
    );
  }

  return (
    <ClientRealLayout>
      <style>{`
        .PhoneInputWrapper .PhoneInput {
          border: none;
          border-radius: 0;
          padding: 0;
          background: transparent;
        }
        .PhoneInputWrapper .PhoneInputInput {
          border: none;
          outline: none;
          background: transparent;
          font-size: 14px;
          padding: 0;
          margin-left: 8px;
          flex: 1;
        }
        .PhoneInputWrapper .PhoneInputCountrySelect {
          border: none;
          background: transparent;
          outline: none;
          font-size: 14px;
          margin-right: 8px;
        }
      `}</style>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 -mx-6 -mt-6 px-6 py-4 mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <FaArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">My Account</h1>
              <p className="text-sm text-gray-500 mt-1">Client contact profile</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Profile Information */}
          <section className="lg:col-span-2 bg-white rounded-lg border border-border shadow-sm p-6">
            <h2 className="text-sm font-semibold text-gray-800 mb-4 border-b pb-2">
              Profile Information
            </h2>

            {/* Profile Picture */}
            <div className="flex items-start gap-4 mb-6">
              <div className="relative w-16 h-16">
                {form.profile_picture ? (
                  <img
                    src={resolveBackendUrl(form.profile_picture)}
                    alt="Profile"
                    className="w-16 h-16 rounded-full object-cover border-2 border-gray-300"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextElementSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <div
                  className={`w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-lg font-semibold text-gray-600 overflow-hidden ${
                    form.profile_picture ? "hidden" : ""
                  }`}
                >
                  {fullName
                    .split(" ")
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((p) => p[0])
                    .join("")
                    .toUpperCase() || "CL"}
                </div>
                {/* Camera Icon Overlay */}
                <label className="absolute bottom-0 right-0 bg-red-600 w-6 h-6 rounded-full flex items-center justify-center cursor-pointer border-2 border-white hover:bg-red-700">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    disabled={saving}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 7h4l3-3h4l3 3h4v12H3V7z"
                    />
                  </svg>
                </label>
              </div>
              <div className="flex-1">
                <p className="text-base font-semibold text-gray-900">
                  {fullName}
                </p>
                {form.email && (
                  <p className="text-sm text-gray-500 mt-1">{form.email}</p>
                )}
              </div>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.first_name}
                  onChange={handleChange("first_name")}
                  className={`w-full border ${
                    formErrors.first_name
                      ? "border-red-500"
                      : "border-gray-200"
                  } rounded px-3 py-2 text-sm focus:ring-1 focus:ring-red-400 focus:outline-none`}
                />
                {formErrors.first_name && (
                  <p className="text-xs text-red-500 mt-1">
                    {formErrors.first_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.last_name}
                  onChange={handleChange("last_name")}
                  className={`w-full border ${
                    formErrors.last_name ? "border-red-500" : "border-gray-200"
                  } rounded px-3 py-2 text-sm focus:ring-1 focus:ring-red-400 focus:outline-none`}
                />
                {formErrors.last_name && (
                  <p className="text-xs text-red-500 mt-1">
                    {formErrors.last_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={handleChange("title")}
                  placeholder="e.g., Manager, Supervisor"
                  className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-red-400 focus:outline-none"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={handleChange("email")}
                  className={`w-full border ${
                    formErrors.email ? "border-red-500" : "border-gray-200"
                  } rounded px-3 py-2 text-sm focus:ring-1 focus:ring-red-400 focus:outline-none`}
                />
                {formErrors.email && (
                  <p className="text-xs text-red-500 mt-1">
                    {formErrors.email}
                  </p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-500 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div
                  className={`PhoneInputWrapper border ${
                    formErrors.phone_mobile
                      ? "border-red-500"
                      : "border-gray-200"
                  } rounded focus-within:ring-1 focus-within:ring-red-400`}
                >
                  <PhoneInput
                    international
                    defaultCountry="CA"
                    value={form.phone_mobile || ""}
                    onChange={(value) => {
                      // PhoneInput passes the value directly, not an event
                      setForm((prev) => ({ ...prev, phone_mobile: String(value || "") }));
                      if (formErrors.phone_mobile) {
                        setFormErrors((prev) => ({ ...prev, phone_mobile: "" }));
                      }
                    }}
                    className="px-3 py-2"
                  />
                </div>
                {formErrors.phone_mobile && (
                  <p className="text-xs text-red-500 mt-1">
                    {formErrors.phone_mobile}
                  </p>
                )}
              </div>
            </div>

            {/* Save Button */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="bg-buttonRed hover:bg-buttonRed-hover text-white px-6 py-2 rounded-3xl text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {saving ? "SAVING..." : "SAVE"}
              </button>
            </div>
          </section>

          {/* Right: Sign-in & Security */}
          <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 flex flex-col">
            <h2 className="text-sm font-semibold text-gray-800 mb-4 border-b pb-2">
              Sign-in &amp; Security
            </h2>

            <div className="space-y-4 text-sm text-gray-700 flex-1">
              {/* Email */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-800">Email</span>
                </div>
                <p className="text-gray-600 break-all">
                  {securityData.email || form.email || "—"}
                </p>
              </div>

              {/* Password */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-800">Password</p>
                </div>
                <button
                  type="button"
                  onClick={() => setOpenModal("reset")}
                  className="text-xs font-semibold text-buttonRed hover:underline"
                >
                  Change Password
                </button>
              </div>

              {/* Last Sign-In */}
              <div className="pt-2 border-t border-gray-200">
                <p className="font-medium text-gray-800 mb-1">Last Sign-In</p>
                <p className="text-xs text-gray-500">
                  {formatAppDateTime(securityData.last_signin, { emptyValue: "Never" })}
                </p>
              </div>

              <TwoStepVerificationSection
                compact
                userDisplayName={`${form.first_name || ""} ${form.last_name || ""}`.trim()}
              />

              {/* Trusted Devices */}
              <div className="pt-2 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-gray-800">Trusted Devices</p>
                  <button
                    onClick={() => setOpenModal("devices")}
                    className="text-xs font-semibold text-buttonRed hover:underline"
                  >
                    Manage
                  </button>
                </div>
                {securityData.devices && securityData.devices.length > 0 ? (
                  <p className="text-xs text-gray-500">
                    {securityData.devices.length} device
                    {securityData.devices.length !== 1 ? "s" : ""} trusted
                  </p>
                ) : (
                  <p className="text-xs text-gray-500">No trusted devices</p>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        open={openModal === "unsaved"}
        onSaveAndRedirect={handleSaveAndRedirect}
        onRedirectWithoutSaving={handleRedirectWithoutSaving}
        onCancel={handleCancelUnsaved}
        saving={saving}
      />

      {/* Reset Password Modal */}
      {openModal === "reset" && (
        <ResetPasswordForm
          onClose={() => setOpenModal(null)}
          onSave={handlePasswordReset}
        />
      )}

      {/* Trusted Devices Modal */}
      {openModal === "devices" && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6 max-h-[80vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">Trusted Devices</h2>
            {securityData.devices && securityData.devices.length > 0 ? (
              <div className="space-y-4">
                {securityData.devices.map((device) => {
                  const deviceInfo = formatDeviceInfo(device);
                  return (
                    <div
                      key={device.trusted_device_id}
                      className="border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {getDeviceTypeIcon(device.device_type, 20)}
                            <span className="text-sm font-medium">
                              {deviceInfo.deviceName}
                            </span>
                            {getOsIcon(device.os_type, 18)}
                            <span className="text-sm text-gray-600">
                              {deviceInfo.osName}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mb-2">
                            {getBrowserIcon(device.browser_type, 18)}
                            <span className="text-sm text-gray-600">
                              {deviceInfo.browserName}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            Last activity:{" "}
                            {formatAppDateTime(device.activity_date, { emptyValue: "Unknown" })}
                          </p>
                        </div>
                        <button
                          onClick={() =>
                            handleRemoveDevice(device.trusted_device_id)
                          }
                          className="text-red-600 hover:text-red-800 text-sm px-3 py-1 rounded border border-red-200 hover:border-red-300"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                No trusted devices found
              </p>
            )}
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setOpenModal(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </ClientRealLayout>
  );
};

export default ClientMyAccount;
